import * as React from 'react';
import { Field, FieldArray, Form, Formik, FormikHelpers as FormikActions } from 'formik';
import FieldRadio from '@magoosh/lib/formik_inputs/field_radio';
import { DangerButton, PrimaryButton } from '@magoosh/lib/button';
import DropdownList from 'react-widgets/lib/DropdownList';
import { adminPaths } from 'config/path_helpers';
import fetch from 'utilities/fetch';
import { LiveClassCohort } from '@magoosh/admin/live_class_sessions/types';
import FieldInput from '@magoosh/lib/formik_inputs/field_input';
import { FontAwesomeIcon } from '@magoosh/lib/icons';
import { Col, Row } from 'react-bootstrap';
import FieldCheckbox from '@magoosh/lib/field_checkbox';
import styles from './style.module.scss';
import { paths } from 'config/path_helpers';

interface FormProps {
  brandingOptions: {
    id: number;
    name: string;
  }[];
  subscriptionPlanOptions: {
    id: number;
    name: string;
  }[];
  cohort?: LiveClassCohort;
  runQuery: any;
  closeModal: () => void;
}

interface CohortFormValues {
  name: string;
  attachableType: 'Branding' | 'SubscriptionPlan';
  brandings: { id: number; name: string }[];
  subscriptionPlans: { id: number; name: string }[];
  notLoggedInDescription?: string;
  loggedInDescription?: string;
  active: boolean;
  onDemand: boolean;
  createClassSection: boolean;
}

const CohortForm: React.FC<FormProps> = (props) => {
  const initialValues: CohortFormValues = {
    name: '',
    attachableType: 'Branding',
    brandings: [],
    subscriptionPlans: [],
    notLoggedInDescription: '',
    loggedInDescription: '',
    active: false,
    onDemand: false,
    createClassSection: true
  };

  if (props.cohort) {
    initialValues.name = props.cohort.name;
    initialValues.attachableType = props.cohort.attachableType;
    initialValues.notLoggedInDescription = props.cohort.notLoggedInDescription;
    initialValues.loggedInDescription = props.cohort.loggedInDescription;
    initialValues.active = props.cohort.active;
    if (props.cohort.attachableType === 'Branding') {
      initialValues.brandings = props.cohort.brandings;
    } else {
      initialValues.subscriptionPlans = props.cohort.subscriptionPlans;
    }

    initialValues.onDemand = props.cohort.onDemand;
    initialValues.createClassSection = false;
  }

  const onSubmitForm = (values: CohortFormValues, { setSubmitting }: FormikActions<CohortFormValues>) => {
    setSubmitting(true);

    fetch(
      props.cohort ? adminPaths.api.liveClassCohort(props.cohort.id) : adminPaths.api.liveClassCohorts(),
      {
        method: props.cohort ? 'PATCH' : 'POST',
        body: JSON.stringify(values)
      }
    )
      .then((response) => {
        props.closeModal();
        props.runQuery();
      })
      .catch((response) => alert(response.errors._error))
      .finally(() => setSubmitting(false));
  };

  return (
    <Formik onSubmit={onSubmitForm} initialValues={initialValues}>
      {({ values, isSubmitting, submitForm, setFieldValue }) => (
        <Form>
          <div>
            <label htmlFor="cohort-name">Name</label>
            <Field name="name" className="form-control u-margin-B-s" id="cohort-name" label="Name" />
            <FieldCheckbox name="active" id="cohort-active" checked={String(values.active) === 'true'}>
              Active
            </FieldCheckbox>
            <label>Cohort Type</label>
            <FieldRadio
              name="attachableType"
              id="attachable-type-branding"
              value="Branding"
              checked={values.attachableType === 'Branding'}>
              Branding
            </FieldRadio>
            <FieldRadio
              name="attachableType"
              id="attachable-type-plan"
              value="SubscriptionPlan"
              checked={values.attachableType === 'SubscriptionPlan'}>
              Subscription Plan
            </FieldRadio>
            {renderBrandingInputs(values, props.brandingOptions, setFieldValue)}
            {renderSubscriptionPlanInputs(values, props.subscriptionPlanOptions, setFieldValue)}
            <>
              <FieldInput
                label="Description HTML (not signed in)"
                type="textarea"
                name="notLoggedInDescription"
              />
              <small>HTML rendered on the live classes page for users who aren't signed in.</small>
              <FieldInput label="Description HTML (signed in)" type="textarea" name="loggedInDescription" />
              <small>HTML rendered on the live classes page for signed in students.</small>
            </>
            <hr />
            <FieldCheckbox name="onDemand" id="cohort-on-demand" checked={String(values.onDemand) === 'true'}>
              On Demand cohort
            </FieldCheckbox>
            {renderClassSectionSection(props.cohort, values)}
            <div>
              <PrimaryButton className="u-margin-T-s" submitting={isSubmitting} onClick={submitForm}>
                Submit
              </PrimaryButton>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

function renderBrandingInputs(values, brandingOptions, setFieldValue) {
  if (!(values.attachableType === 'Branding')) {
    return null;
  }

  return (
    <>
      <FieldArray
        name="brandings"
        render={(arrayHelpers) => {
          const brandings = values.brandings;
          return (
            <div>
              {brandings.map((branding, index) => {
                return (
                  <Row>
                    <Col xs={10}>
                      <DropdownList
                        key={`brandings[${index}].id`}
                        name={`brandings[${index}].id`}
                        className="u-margin-B-xs"
                        data={brandingOptions}
                        filter="contains"
                        placeholder="Select a Branding"
                        textField="name"
                        valueField="id"
                        dataKey="id"
                        value={branding.id}
                        onChange={(item) => setFieldValue(`brandings[${index}].id`, item.id)}
                      />
                    </Col>
                    <Col xs={1}>
                      <DangerButton onClick={() => arrayHelpers.remove(index)}>
                        <FontAwesomeIcon icon="times"></FontAwesomeIcon>
                      </DangerButton>
                    </Col>
                  </Row>
                );
              })}
              <PrimaryButton onClick={() => arrayHelpers.push({ id: null })}>Add Branding</PrimaryButton>
            </div>
          );
        }}
      />
    </>
  );
}

function renderSubscriptionPlanInputs(values, subscriptionPlanOptions, setFieldValue) {
  if (!(values.attachableType === 'SubscriptionPlan')) {
    return null;
  }

  return (
    <>
      <div className="alert alert-banner">
        Cohorts need to be attached to a subscription plan to show up as an option on the checkout page. It
        doesn't matter which plan they are attached to, as long as it's an <strong>active plan</strong> in
        that branding. For example, all upcoming cohorts for all active plans in GMAT will be shown on any
        GMAT live classes plan checkout page, no matter which plan is being purchased.
      </div>
      <FieldArray
        name="subscriptionPlans"
        render={(arrayHelpers) => {
          const plans = values.subscriptionPlans;
          return (
            <div>
              {plans.map((plan, index) => {
                return (
                  <Row>
                    <Col xs={10}>
                      <DropdownList
                        key={`subscriptionPlans[${index}].id`}
                        name={`subscriptionPlans[${index}].id`}
                        className="u-margin-B-xs"
                        data={subscriptionPlanOptions}
                        filter="contains"
                        placeholder="Select a Subscription Plan"
                        textField="name"
                        valueField="id"
                        dataKey="id"
                        value={plan.id}
                        onChange={(item) => setFieldValue(`subscriptionPlans[${index}].id`, item.id)}
                      />
                    </Col>
                    <Col xs={1}>
                      <DangerButton onClick={() => arrayHelpers.remove(index)}>
                        <FontAwesomeIcon icon="times"></FontAwesomeIcon>
                      </DangerButton>
                    </Col>
                  </Row>
                );
              })}
              <PrimaryButton className={'u-margin-T-xs'} onClick={() => arrayHelpers.push({ id: null })}>
                Add Subscription Plan
              </PrimaryButton>
            </div>
          );
        }}
      />
    </>
  );
}

function renderClassSectionSection(liveClassCohort: LiveClassCohort, values: CohortFormValues) {
  if (liveClassCohort?.classSectionId) {
    return (
      <div className={styles.classSection}>
        <a href={paths.partner.class(liveClassCohort.classSectionId)} target={'educator-portal'}>
          Class section
        </a>
      </div>
    );
  } else {
    return (
      <div className={styles.classSection}>
        <FieldCheckbox
          name="createClassSection"
          id="cohort-create-class-section"
          checked={String(values.createClassSection) === 'true'}>
          Create a class section for this cohort
        </FieldCheckbox>
      </div>
    );
  }
}

export default CohortForm;
